import { stringToPath } from 'remeda';
import { TFunction } from 'i18next';
import { formatAuthorData } from 'utility/functions';
import { BaseUserAccountSummary, FullEditorialAsset } from 'utility/types/dataProvider';
import {
  plaintextToolbarButtons,
  defaultToolbarButtons,
  inlineBylineToolbarButtons,
} from 'components/EditorBar/const';
import { EditorToolbarOptions } from 'components/EditorBar/types';
import { AssetType } from 'utility/types/cosmosTypes';
import { Content, JSONContent } from '@tiptap/core';
import { formatTiptapAuthorTag } from '../components/common/Authors';
import { GenericItemLSS } from 'components/FormControls/ListSearchSelect/ListSearchSelect';
import { isNumber } from 'remeda';

export type EditorType =
  | 'body'
  | 'inline'
  | 'number'
  | 'coordinate'
  | 'richtext'
  | 'text'
  | 'textarea'
  | 'code'
  | 'array'
  | 'custom'
  | 'hidden';

export type EditorSetupConfig = {
  /**
   * type used to determine extensions config
   * and useDebouncedOnChange switch handler
   *
   * Note: 'hidden' type sometimes used for collab
   * the editor not rendered in most cases but instead
   * being used as a state holder for collab purposes
   * which syncs some field data across collab users..
   **/
  type?: EditorType;
  /**
   * name of the field for form
   * format / setContent / RichTextInput purposes
   * used this to register to ReactHookForm
   * or set '' to not register with rhf if needed
   * also for collab extensions name is required
   * to register as the yjs fragment field
   **/
  name?: string;
  /**
   * what to pass into the editor initially
   * note on number types from the record
   * should be converted to strings here
   * useCreateEditor initial content helpers
   * will coerce any numbers to strings
   */
  contentForEditor?: Content;
  /**
   * the data of the field in the fetched record
   * sometimes also used as initial content
   **/
  recordFieldData?: string | number | null;
  /** Initial placeholder text for the editor */
  placeholder?: string;
  /* Other props main usage is for UI Components like <RichTextInput /> */
  id?: string;
  /* Field display label for UI and AI Reference */
  label?: string;
  maxLength?: number;
  recommendedLength?: number;
  showCount?: boolean | 'word' | 'both';
  /** Tiptap editor is directly user editable? */
  editable?: boolean;
  toolbarButtons?: EditorToolbarOptions;
  /** Disable DropCursor extension in StarterKit to prevent conflicts with custom drag-drop handlers */
  disableDropCursor?: boolean;
};

// todo: toolbarButtons: in future, would be nice
// to have the extensions per editor directly defined on
// the configs here, as it would be clearer to understand
// what editors get paired with what toolbar
// buttons based on the set of extensions
// rather than us defining it manually here

// ------------------------------------------
// Helper Utility Functions
// ------------------------------------------

/**
 * Retrieves a value from an object via a given path array.
 * To be used with remedas stringToPath helper function.
 * @param obj - The object to retrieve the value from.
 * @param pathArray - The array of keys representing the path.
 * @returns The value at the given path or undefined if not found.
 * @returns Undefined if no obj or no pathArray.
 * @example getContentViaPath(obj, stringToPath('date.nested.path'));
 */
function getContentViaPath(obj?: Record<string, any>, pathArray?: string[]): any {
  if (!obj || !Array.isArray(pathArray) || !pathArray?.length) {
    console.warn('[getContentViaPath] - Invalid parameters');
    return undefined;
  }
  const result = pathArray.reduce((acc, key) => acc?.[key], obj);
  return result;
}

/**
 * Convert TipTap getText to array
 **/
export function convertEditorTextToArray(editorText: string) {
  if (!editorText) {
    return [];
  }
  // Split the editorText by newline character and filter out empty strings
  const itemsArray = editorText?.split('\n').filter((word) => word?.trim() !== '');
  return itemsArray; // Return array of items
}

/**
 * Convert textual string to paragraphs for tiptap
 **/
export function convertStringToTextareaParagraphs(
  editorText: string,
  keepEmptyStrings = false,
): string {
  if (!editorText) {
    return '';
  }

  // Text already contains <p> tags, return as is
  if (editorText.includes('<p>') || editorText.includes('<p ')) {
    return editorText;
  }

  // Split text by newline character
  let paragraphs = editorText.split('\n');

  // Optionally filter out empty strings
  if (!keepEmptyStrings) {
    paragraphs = paragraphs.filter((keyword) => keyword?.trim() !== '');
  }

  // Wrap each line in <p> tags and join them together
  const paragraphString = paragraphs
    .map((keyword) => `<p>${keyword?.trim()}</p>`)
    .join('');

  return paragraphString; // Return string of paragraphs
}

/**
 * Remove <p> tags and replace them with newline
 **/
export function convertParagraphsToNewline(editorText: string) {
  if (!editorText) {
    return '';
  }

  const newString = editorText
    ?.replace(/<\/p>/g, '\n') // Replace closing </p> tags with newline
    ?.replace(/<p>/g, '') // Remove opening <p> tags
    ?.trim(); // Trim any leading or trailing whitespace

  return newString;
}

/**
 * Convert paragraph string to array
 */
export function convertParagraphsToParagraphArray(paragraphString: string) {
  if (!paragraphString) {
    return [];
  }

  // Split by the <p> tag and filter out empty strings
  const paragraphsArray = paragraphString
    .split('<p>')
    .filter((paragraph) => paragraph?.trim() !== '')
    .map((paragraph) => paragraph?.replace('</p>', '')); // Remove closing </p> tag

  return paragraphsArray;
}

/**
 * Utility to ensure whatever value is passed in
 * is results in an output converted to a string
 * or null if not valid conversion. Can be used
 * to enforce string values or throw out nulls.
 *
 * - valid strings are trimmed and returned
 * - empty strings return null
 * - numbers are cast to strings
 * - NaN or Infinity numbers return null
 * - null, undefined, obj, bools, return null
 *
 * null, undefined, empty strings
 * objects and booleans not valid
 *
 * returns: trimmed string or null
 */

export function toStringOrNull(value: any) {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') {
      return null; // null empty strings
    }
    return trimmed;
  }

  if (typeof value === 'number') {
    // NaN or Infinity checks
    const isValid = Number.isFinite(value) && isNumber(value);
    if (!isValid) {
      return null;
    }
    // convert number to string
    const text = String(value).trim();
    if (text === '') {
      return null; // null empty strings
    }
    return text;
  }

  // all else: objects, bools, null, undefined... is null
  return null;
}

/**
 * Convert an array of strings/numbers into HTML paragraphs.
 * Returns a concat string of html p tags or null if no valid items
 */
export function convertArrayToParagraphs(
  arr?: Array<string | number | null | undefined>,
): string | null {
  // nothing to do if not array or is empty
  if (!Array.isArray(arr) || arr.length === 0) {
    return null;
  }

  // Map the array to HTML paragraphs
  // numbers, null, undefined, empty strings
  // objects and booleans not valid
  // skip nulls and filter out after map
  const paragraphs = arr
    .map((item) => {
      const text = toStringOrNull(item);
      if (text === null) {
        return null;
      }
      // wrap final text in <p> tags
      return `<p>${text}</p>`;
    })
    .filter((text) => text !== null);

  // If everything got filtered out, return null
  if (paragraphs.length === 0) {
    return null;
  }

  // Join into one html string
  const result = paragraphs.join('');

  return result;
}

/**
 * UserAccountSummary -> GenericItemLSS
 * for <Authors> Users as ListSearchSelect items
 */
export function formatUserToGenericItemLSS(user: BaseUserAccountSummary): GenericItemLSS {
  return {
    id: user?.uuid,
    path: user?.uuid,
    name: formatAuthorData(user, { includeEmail: true }),
    label: formatAuthorData(user, { includeEmail: true }),
    description: user?.email,
    // Preserve additional properties needed for author display
    account_type: user?.account_type,
    first_name: user?.first_name,
    last_name: user?.last_name,
    screen_name: user?.screen_name,
  };
}

// ------------------------------------------
// Editor Config Functions
// ------------------------------------------

export function getAuthorsConfig({
  record,
  isReadOnlyMode,
  isImportMode,
  t,
}: EditorConfigArgs): EditorSetupConfig {
  const pathName = 'authors';
  const pathArray = stringToPath(pathName);
  const pathContent = getContentViaPath(record, pathArray); // Should be a UserAccountSummary[]
  // Expect pathContent to be an array; if not, default to an empty array.
  const authorsRaw: BaseUserAccountSummary[] = Array.isArray(pathContent)
    ? pathContent
    : [];
  // On the FullEditorialAssets, authors is an array of UserAccountSummary
  // we will need to map that to our GenericItemLSS type for the select dialog
  // use the formatUserAccountSummaryToGenericItemLSS helper
  const authorsAsLssItems = authorsRaw.map(formatUserToGenericItemLSS);
  // Map the reformatted objects to one HTML string for tiptap custom tags
  // that have uuids and data-json attributes, as it will be needed
  // for communicating across collab in LSS select dialog and init render.
  const tiptapContent = authorsAsLssItems
    .map((author) => formatTiptapAuthorTag(author))
    .join('');

  const getPlaceholder =
    record?.type === 'article'
      ? t?.('blox.editorial.asset.authors.label')
      : record?.type === 'video'
        ? t?.('blox.editorial.asset.creators.label')
        : t?.('blox.editorial.asset.authors.help');

  return {
    name: pathName,
    id: pathName,
    recordFieldData: pathContent,
    contentForEditor: tiptapContent,
    type: 'array',
    placeholder: getPlaceholder,
    editable: false, // array types are not directly editable - uses select dialog
  };
}

/**
 * Directly structure a JSONContent object
 * used as the initial content in the editor.
 *
 * HTML 'editor' is an illusion, it is one tiptap code block
 * that the user cannot exit out of, so content must be set as
 * JSONContent codeBlock node - not a plain string.
 *
 * type JSONContent = {
 *   type?: string;
 *   attrs?: { ... };
 *   content?: JSONContent[];
 *   marks?: { type: string; attrs?: { ... }; }[];
 *   text?: string;
 * };
 */

type TBuildHtmlCodeblockNodeArgs = {
  content?: string | null;
};

export function buildHtmlCodeblockNode({
  content,
}: TBuildHtmlCodeblockNodeArgs): JSONContent {
  // Building a tiptap "Content" object from the record data
  // to be directly emplaced as the initial content in the editor
  const nContent = typeof content === 'string' ? content.trim() : '';

  // Note: The entire editors content is one codeblock node.
  // If contentString is empty, set content to an empty array
  // to avoid "Invalid JSON content: Empty text nodes are not allowed"
  // The type doc is neccesary if running this through generateTiptapHtml
  const tiptapContent: JSONContent = {
    type: 'doc',
    content: [
      {
        type: 'codeBlock',
        attrs: { language: 'html' },
        content: nContent ? [{ type: 'text', text: nContent }] : [],
      },
    ],
  };

  return tiptapContent;
}

// Html Assets Create/Edit
export function getHtmlConfig({
  record,
  isReadOnlyMode,
  isImportMode,
}: EditorConfigArgs): EditorSetupConfig {
  const pathName = 'properties.html';
  const pathArray = stringToPath(pathName);

  // Should be a string of html content...
  const pathContent = getContentViaPath(record, pathArray);

  // Build singular 'codeblock' node for 'html editor'
  const tiptapContent = buildHtmlCodeblockNode({ content: pathContent });

  return {
    name: pathName,
    id: pathName,
    recordFieldData: pathContent,
    contentForEditor: tiptapContent,
    type: 'code',
    placeholder: 'Enter HTML Code...',
    editable: !isReadOnlyMode,
  };
}

/**
 * ID for ThreadsBubble or handling edge exceptions around article body copy
 */
export const ARTICLE_BODY_COPY_ID = 'body-copy-content';

type TGetContentFieldLabelArgs = {
  recordType: AssetType | undefined;
  t?: TFunction;
};

function getContentFieldLabel({ recordType, t }: TGetContentFieldLabelArgs): string {
  switch (recordType) {
    case 'article': {
      return t?.('blox.editorial.asset.body_copy.label') || 'Body Copy';
    }
    case 'image': {
      return t?.('blox.editorial.asset.caption.label') || 'Caption';
    }
    case 'audio':
    case 'collection':
    case 'file':
    case 'html':
    case 'link':
    case 'poll':
    case 'table':
    case 'video': {
      return t?.('blox.editorial.asset.description.label') || 'Description';
    }
    default: {
      return 'Content';
    }
  }
}

/**
 * Get main content editor setup config
 */
export function getContentConfig({
  record,
  isReadOnlyMode,
  t,
}: EditorConfigArgs): EditorSetupConfig {
  const assetType = record?.type as AssetType;
  const pathName = 'content';
  const pathArray = stringToPath(pathName);
  const pathContent = getContentViaPath(record, pathArray);

  // If this is an article, use our const for the id as it has implications
  // for other configurations and components in the editor extensions and bubble menus
  const setupId = assetType === 'article' ? ARTICLE_BODY_COPY_ID : pathName;

  const richTextAssetTypes = ['table', 'collection', 'image', 'file', 'link', 'video'];
  const isRichTextAsset = richTextAssetTypes.includes(assetType);

  const fieldLabel = getContentFieldLabel({ recordType: assetType, t: t });

  return {
    name: pathName,
    id: setupId,
    recordFieldData: pathContent,
    contentForEditor: pathContent,
    editable: !isReadOnlyMode,
    type: assetType === 'article' ? 'body' : isRichTextAsset ? 'richtext' : 'text',
    label: fieldLabel,
    placeholder:
      assetType === 'article'
        ? 'Body Copy'
        : assetType === 'image'
          ? t?.('blox.editorial.asset.caption.help')
          : 'Enter a description...',
    toolbarButtons:
      assetType === 'article'
        ? 'all'
        : isRichTextAsset
          ? defaultToolbarButtons
          : plaintextToolbarButtons,
    disableDropCursor: assetType !== 'article',
  };
}

export function getHammerConfig({
  record,
  isReadOnlyMode,
  isImportMode,
}: EditorConfigArgs): EditorSetupConfig {
  const pathName = 'properties.hammer';
  const pathArray = stringToPath(pathName);
  const pathContent = getContentViaPath(record, pathArray);

  return {
    name: pathName,
    id: pathName,
    recordFieldData: pathContent,
    contentForEditor: pathContent,
    type: 'text',
    placeholder: 'Hammer',
    editable: !isReadOnlyMode,
    toolbarButtons: plaintextToolbarButtons,
  };
}

export function getKickerConfig({
  record,
  isReadOnlyMode,
  isImportMode,
}: EditorConfigArgs): EditorSetupConfig {
  const pathName = 'properties.kicker';
  const pathArray = stringToPath(pathName);
  const pathContent = getContentViaPath(record, pathArray);

  return {
    name: pathName,
    id: pathName,
    recordFieldData: pathContent,
    contentForEditor: pathContent,
    type: 'text',
    placeholder: 'Kicker',
    editable: !isReadOnlyMode,
    toolbarButtons: plaintextToolbarButtons,
  };
}

export function getTitleConfig({
  record,
  isReadOnlyMode,
  isImportMode,
  t,
}: EditorConfigArgs): EditorSetupConfig {
  const assetType = record?.type as AssetType;
  const pathName = 'title';
  const pathArray = stringToPath(pathName);
  const pathContent = getContentViaPath(record, pathArray);

  return {
    name: pathName,
    id: pathName,
    recordFieldData: pathContent,
    contentForEditor: pathContent,
    type: 'text',
    editable: !isReadOnlyMode,
    toolbarButtons: plaintextToolbarButtons,
    placeholder:
      assetType === 'article'
        ? t?.('blox.editorial.asset.title.label')
        : assetType === 'poll'
          ? t?.('blox.editorial.asset.title.help_alt_poll')
          : t?.('blox.editorial.asset.title.help_alt'),
  };
}

export function getBylineConfig({
  record,
  isReadOnlyMode,
  isImportMode,
  t,
}: EditorConfigArgs): EditorSetupConfig {
  const assetType = record?.type as AssetType;
  const pathName = 'byline';
  const pathArray = stringToPath(pathName);
  const pathContent = getContentViaPath(record, pathArray);

  return {
    name: pathName,
    id: pathName,
    recordFieldData: pathContent,
    contentForEditor: pathContent,
    editable: !isReadOnlyMode,
    toolbarButtons: inlineBylineToolbarButtons,
    type: 'richtext',
    placeholder:
      assetType === 'article'
        ? t?.('blox.editorial.asset.byline.label')
        : t?.('blox.editorial.asset.byline.help'),
  };
}

export function getSubheadlineConfig({
  record,
  isReadOnlyMode,
  isImportMode,
}: EditorConfigArgs): EditorSetupConfig {
  const pathName = 'properties.subheadline';
  const pathArray = stringToPath(pathName);
  const pathContent = getContentViaPath(record, pathArray);

  return {
    name: pathName,
    id: pathName,
    recordFieldData: pathContent,
    contentForEditor: pathContent,
    editable: !isReadOnlyMode,
    type: 'text',
    placeholder: 'Subheadline',
    toolbarButtons: plaintextToolbarButtons,
  };
}

// Link Assets Create/Edit
export function getLinkConfig({
  record,
  isReadOnlyMode,
  isImportMode,
}: EditorConfigArgs): EditorSetupConfig {
  const pathName = 'properties.link';
  const pathArray = stringToPath(pathName);
  const pathContent = getContentViaPath(record, pathArray);

  return {
    name: pathName,
    id: pathName,
    recordFieldData: pathContent,
    contentForEditor: pathContent,
    editable: !isReadOnlyMode,
    type: 'text',
    placeholder: 'https://www.example.com/',
  };
}

export function getSectionsConfig({
  record,
  isReadOnlyMode,
  isImportMode,
}: EditorConfigArgs): EditorSetupConfig {
  // syndication uses a different path name apparently
  const pathName = isImportMode ? 'categories' : 'sections';
  const pathArray = stringToPath(pathName);
  const pathContent = getContentViaPath(record, pathArray);
  const tiptapContent = convertArrayToParagraphs(pathContent);

  return {
    name: pathName,
    id: pathName,
    recordFieldData: pathContent,
    contentForEditor: tiptapContent,
    type: 'array',
    placeholder: 'Add Sections',
    editable: false, // array types are not directly editable - uses select dialog
  };
}

export function getSummaryConfig({
  record,
  isReadOnlyMode,
  isImportMode,
}: EditorConfigArgs): EditorSetupConfig {
  const pathName = 'summary';
  const pathArray = stringToPath(pathName);
  const pathContent = getContentViaPath(record, pathArray);

  return {
    name: pathName,
    id: pathName,
    recordFieldData: pathContent,
    contentForEditor: pathContent,
    editable: !isReadOnlyMode,
    recommendedLength: 200,
    type: 'richtext',
    showCount: 'both',
  };
}

export function getKeywordsConfig({
  record,
  isReadOnlyMode,
  isImportMode,
}: EditorConfigArgs): EditorSetupConfig {
  const pathName = 'keywords';
  const pathArray = stringToPath(pathName);
  const pathContent = getContentViaPath(record, pathArray);
  const tiptapContent = convertArrayToParagraphs(pathContent);
  return {
    name: pathName,
    id: pathName,
    recordFieldData: pathContent,
    contentForEditor: tiptapContent,
    type: 'array',
    placeholder: 'Add Keywords',
    editable: false, // array types are not directly editable - uses select dialog
  };
}

export function getSiteTagsConfig({
  record,
  isReadOnlyMode,
  isImportMode,
}: EditorConfigArgs): EditorSetupConfig {
  const pathName = 'site_tags';
  const pathArray = stringToPath(pathName);
  const pathContent = getContentViaPath(record, pathArray);
  const tiptapContent = convertArrayToParagraphs(pathContent);
  return {
    name: pathName,
    id: pathName,
    recordFieldData: pathContent,
    contentForEditor: tiptapContent,
    type: 'array',
    placeholder: 'Add Site Tags',
    editable: false, // array types are not directly editable - uses select dialog
  };
}

export function getFlagsConfig({
  record,
  isReadOnlyMode,
  isImportMode,
}: EditorConfigArgs): EditorSetupConfig {
  const pathName = 'flags';
  const pathArray = stringToPath(pathName);
  const pathContent = getContentViaPath(record, pathArray);
  const tiptapContent = convertArrayToParagraphs(pathContent);

  return {
    name: pathName,
    id: pathName,
    recordFieldData: pathContent,
    contentForEditor: tiptapContent,
    type: 'array',
    placeholder: 'Add Flags',
    editable: false, // array types are not directly editable - uses select dialog
  };
}

export function getPriorityConfig({
  record,
  isReadOnlyMode,
  isImportMode,
}: EditorConfigArgs): EditorSetupConfig {
  const pathName = 'properties.priority';
  const pathArray = stringToPath(pathName);
  const pathContent = getContentViaPath(record, pathArray);

  return {
    name: pathName,
    id: pathName,
    recordFieldData: pathContent,
    contentForEditor: pathContent,
    type: 'number',
    placeholder: '0',
    editable: !isReadOnlyMode,
  };
}

export function getSourceConfig({
  record,
  isReadOnlyMode,
  isImportMode,
}: EditorConfigArgs): EditorSetupConfig {
  const pathName = 'properties.source';
  const pathArray = stringToPath(pathName);
  const pathContent = getContentViaPath(record, pathArray);

  return {
    name: pathName,
    id: pathName,
    recordFieldData: pathContent,
    contentForEditor: pathContent,
    type: 'text',
    editable: !isReadOnlyMode,
  };
}

export function getPrintHeadlineConfig({
  record,
  isReadOnlyMode,
  isImportMode,
}: EditorConfigArgs): EditorSetupConfig {
  const pathName = 'properties.print_headline';
  const pathArray = stringToPath(pathName);
  const pathContent = getContentViaPath(record, pathArray);

  return {
    name: pathName,
    id: pathName,
    recordFieldData: pathContent,
    contentForEditor: pathContent,
    type: 'text',
    editable: !isReadOnlyMode,
    showCount: true,
  };
}

export function getPrintJumpHeadConfig({
  record,
  isReadOnlyMode,
  isImportMode,
}: EditorConfigArgs): EditorSetupConfig {
  const pathName = 'properties.print_jump_head';
  const pathArray = stringToPath(pathName);
  const pathContent = getContentViaPath(record, pathArray);

  return {
    name: pathName,
    id: pathName,
    recordFieldData: pathContent,
    contentForEditor: pathContent,
    type: 'text',
    editable: !isReadOnlyMode,
  };
}

export function getPrintJumpKeywordConfig({
  record,
  isReadOnlyMode,
  isImportMode,
}: EditorConfigArgs): EditorSetupConfig {
  const pathName = 'properties.print_jump_keyword';
  const pathArray = stringToPath(pathName);
  const pathContent = getContentViaPath(record, pathArray);

  return {
    name: pathName,
    id: pathName,
    recordFieldData: pathContent,
    contentForEditor: pathContent,
    type: 'text',
    editable: !isReadOnlyMode,
  };
}

export function getPrintNotesConfig({
  record,
  isReadOnlyMode,
  isImportMode,
}: EditorConfigArgs): EditorSetupConfig {
  const pathName = 'properties.print_notes';
  const pathArray = stringToPath(pathName);
  const pathContent = getContentViaPath(record, pathArray);

  return {
    name: pathName,
    id: pathName,
    recordFieldData: pathContent,
    contentForEditor: pathContent,
    type: 'text',
    showCount: true,
    editable: !isReadOnlyMode,
  };
}

export function getPrintPlacementPriorityConfig({
  record,
  isReadOnlyMode,
  isImportMode,
}: EditorConfigArgs): EditorSetupConfig {
  const pathName = 'properties.print_placement_priority';
  const pathArray = stringToPath(pathName);
  const pathContent = getContentViaPath(record, pathArray);

  return {
    name: pathName,
    id: pathName,
    recordFieldData: pathContent,
    contentForEditor: pathContent,
    type: 'number',
    editable: !isReadOnlyMode,
  };
}

export function getPrintSlugConfig({
  record,
  isReadOnlyMode,
  isImportMode,
}: EditorConfigArgs): EditorSetupConfig {
  const pathName = 'properties.print_slug';
  const pathArray = stringToPath(pathName);
  const pathContent = getContentViaPath(record, pathArray);

  return {
    name: pathName,
    id: pathName,
    recordFieldData: pathContent,
    contentForEditor: pathContent,
    placeholder: 'Slug',
    type: 'text',
    editable: !isReadOnlyMode,
  };
}

export function getPrintSubheadlineConfig({
  record,
  isReadOnlyMode,
  isImportMode,
}: EditorConfigArgs): EditorSetupConfig {
  const pathName = 'properties.print_subheadline';
  const pathArray = stringToPath(pathName);
  const pathContent = getContentViaPath(record, pathArray);

  return {
    name: pathName,
    id: pathName,
    recordFieldData: pathContent,
    contentForEditor: pathContent,
    type: 'text',
    editable: !isReadOnlyMode,
    showCount: true,
  };
}

// Location
export function getAddressConfig({
  record,
  isReadOnlyMode,
  isImportMode,
}: EditorConfigArgs): EditorSetupConfig {
  const pathName = 'properties.address';
  const pathArray = stringToPath(pathName);
  const pathContent = getContentViaPath(record, pathArray);
  const tiptapContent = convertStringToTextareaParagraphs(pathContent, true);

  return {
    name: pathName,
    id: pathName,
    recordFieldData: pathContent,
    contentForEditor: tiptapContent,
    type: 'textarea',
    editable: !isReadOnlyMode,
  };
}

export function getMunicipalityConfig({
  record,
  isReadOnlyMode,
  isImportMode,
}: EditorConfigArgs): EditorSetupConfig {
  const pathName = 'properties.municipality';
  const pathArray = stringToPath(pathName);
  const pathContent = getContentViaPath(record, pathArray);

  return {
    name: pathName,
    id: pathName,
    recordFieldData: pathContent,
    contentForEditor: pathContent,
    type: 'text',
    editable: !isReadOnlyMode,
  };
}

export function getPostalCodeConfig({
  record,
  isReadOnlyMode,
  isImportMode,
}: EditorConfigArgs): EditorSetupConfig {
  const pathName = 'properties.postal_code';
  const pathArray = stringToPath(pathName);
  const pathContent = getContentViaPath(record, pathArray);

  return {
    name: pathName,
    id: pathName,
    recordFieldData: pathContent,
    contentForEditor: pathContent,
    type: 'text',
    editable: !isReadOnlyMode,
  };
}

export function getLatitudeConfig({
  record,
  isReadOnlyMode,
  isImportMode,
}: EditorConfigArgs): EditorSetupConfig {
  const pathName = 'properties.latitude';
  const pathArray = stringToPath(pathName);
  const pathContent = getContentViaPath(record, pathArray);

  return {
    name: pathName,
    id: pathName,
    recordFieldData: pathContent,
    contentForEditor: pathContent,
    type: 'coordinate',
    editable: !isReadOnlyMode,
  };
}

export function getLongitudeConfig({
  record,
  isReadOnlyMode,
  isImportMode,
}: EditorConfigArgs): EditorSetupConfig {
  const pathName = 'properties.longitude';
  const pathArray = stringToPath(pathName);
  const pathContent = getContentViaPath(record, pathArray);

  return {
    name: pathName,
    id: pathName,
    recordFieldData: pathContent,
    contentForEditor: pathContent,
    type: 'coordinate',
    editable: !isReadOnlyMode,
  };
}

// Tagline - Articles
export function getTaglineConfig({
  record,
  isReadOnlyMode,
  isImportMode,
}: EditorConfigArgs): EditorSetupConfig {
  const pathName = 'properties.tagline';
  const pathArray = stringToPath(pathName);
  const pathContent = getContentViaPath(record, pathArray);

  return {
    name: pathName,
    id: pathName,
    recordFieldData: pathContent,
    contentForEditor: pathContent,
    placeholder: 'Tagline',
    type: 'richtext',
    toolbarButtons: defaultToolbarButtons,
    editable: !isReadOnlyMode,
  };
}

/**
 * SEO Title
 */
export const SEO_TITLE_RECOMMENDED_LENGTH = 60;

export function getSeoTitleConfig({
  record,
  isReadOnlyMode,
  isImportMode,
}: EditorConfigArgs): EditorSetupConfig {
  const pathName = 'properties.seo_title';
  const pathArray = stringToPath(pathName);
  const pathContent = getContentViaPath(record, pathArray);

  return {
    name: pathName,
    id: pathName,
    recordFieldData: pathContent,
    contentForEditor: pathContent,
    type: 'text',
    showCount: true,
    editable: !isReadOnlyMode,
  };
}

export function getCanonicalUrlConfig({
  record,
  isReadOnlyMode,
  isImportMode,
}: EditorConfigArgs): EditorSetupConfig {
  const pathName = 'properties.canonical_url';
  const pathArray = stringToPath(pathName);
  const pathContent = getContentViaPath(record, pathArray);

  return {
    name: pathName,
    recordFieldData: pathContent,
    contentForEditor: pathContent,
    id: pathName,
    type: 'text',
    placeholder: record?.url, // Will be replaced if title stuffing is on
    editable: !isReadOnlyMode,
  };
}

export function getSeoDescriptionConfig({
  record,
  isReadOnlyMode,
  isImportMode,
}: EditorConfigArgs): EditorSetupConfig {
  const pathName = 'properties.seo_description';
  const pathArray = stringToPath(pathName);
  const pathContent = getContentViaPath(record, pathArray);

  return {
    name: pathName,
    recordFieldData: pathContent,
    contentForEditor: pathContent,
    id: pathName,
    type: 'text',
    showCount: true,
    recommendedLength: 200,
    editable: !isReadOnlyMode,
  };
}

export function getUrlTitleConfig({
  record,
  isReadOnlyMode,
  isImportMode,
}: EditorConfigArgs): EditorSetupConfig {
  const pathName = 'properties.url_title';
  const pathArray = stringToPath(pathName);
  const pathContent = getContentViaPath(record, pathArray);
  const maxLen = 200;

  return {
    name: pathName,
    recordFieldData: pathContent,
    contentForEditor: pathContent,
    id: pathName,
    type: 'text',
    maxLength: maxLen,
    showCount: true,
    editable: !isReadOnlyMode,
  };
}

// Social
export function getSocialTitleConfig({
  record,
  isReadOnlyMode,
  isImportMode,
}: EditorConfigArgs): EditorSetupConfig {
  const pathName = 'properties.social_title';
  const pathArray = stringToPath(pathName);
  const pathContent = getContentViaPath(record, pathArray);

  return {
    name: pathName,
    recordFieldData: pathContent,
    contentForEditor: pathContent,
    id: pathName,
    type: 'text',
    recommendedLength: 200,
    showCount: true,
    editable: !isReadOnlyMode,
  };
}

export function getSocialSummaryConfig({
  record,
  isReadOnlyMode,
  isImportMode,
}: EditorConfigArgs): EditorSetupConfig {
  const pathName = 'properties.social_summary';
  const pathArray = stringToPath(pathName);
  const pathContent = getContentViaPath(record, pathArray);

  return {
    name: pathName,
    recordFieldData: pathContent,
    contentForEditor: pathContent,
    id: pathName,
    type: 'textarea',
    recommendedLength: 250,
    showCount: true,
    editable: !isReadOnlyMode,
  };
}

export function getAltTextConfig({
  record,
  isReadOnlyMode,
  t,
}: EditorConfigArgs): EditorSetupConfig {
  const pathName = 'properties.alttext';
  const pathArray = stringToPath(pathName);
  const pathContent = getContentViaPath(record, pathArray);
  const tiptapContent = convertStringToTextareaParagraphs(pathContent, true);

  return {
    name: pathName,
    id: pathName,
    recordFieldData: pathContent,
    contentForEditor: tiptapContent,
    type: 'textarea',
    placeholder: t?.('blox.editorial.asset.properties.altext.help'),
    editable: !isReadOnlyMode,
  };
}

// todo: custom properties dont seem to be using the tiptap editor
// currently, in the future we should move that over
// to use the tiptap editor as well so those fields can be collab
// however there is some custom config going on with the custom properties
// here is an idea on how to communicate over collab mode
// likely similar to Authors where we encode on the data-json
// as the actual <p> tags wont really be displayed to the user
// but we can use the tiptap editor to communicate state data over the wire
// export function getCustomPropertiesConfig(...): EditorSetupConfig {
//   const pathName = 'properties.custom';
//   const pathArray = stringToPath(pathName);
//   const pathContent = getContentViaPath(record, pathArray);
//   const isEditable = !Boolean(isImportMode);
//   // content is array of objects:
//   // [{ "name": "test", "value": "test2"}]
//   const tiptapContent = pathContent ? pathContent.map((item: CustomPropItem) => {return `<p data-json{name:... value:...}></p>`;}).join('') : ';
//   return {
//     name: pathName,
//     recordFieldData: pathContent,
//     contentForEditor: tiptapContent,
//     id: pathName,
//     type: 'array',
//     showCount: true,
//     editable: isEditable,
//   };
// }

export type EditorField =
  | 'authors'
  | 'content'
  | 'title'
  | 'byline'
  | 'sections'
  | 'summary'
  | 'keywords'
  | 'flags'
  | 'site_tags'
  | 'properties.alttext'
  | 'properties.html'
  | 'properties.hammer'
  | 'properties.kicker'
  | 'properties.subheadline'
  | 'properties.link'
  | 'properties.priority'
  | 'properties.source'
  | 'properties.print_headline'
  | 'properties.print_jump_head'
  | 'properties.print_jump_keyword'
  | 'properties.print_notes'
  | 'properties.print_placement_priority'
  | 'properties.print_slug'
  | 'properties.print_subheadline'
  | 'properties.address'
  | 'properties.municipality'
  | 'properties.postal_code'
  | 'properties.latitude'
  | 'properties.longitude'
  | 'properties.tagline'
  | 'properties.seo_title'
  | 'properties.seo_description'
  | 'properties.canonical_url'
  | 'properties.url_title'
  | 'properties.social_title'
  | 'properties.social_summary';

export type EditorConfigArgs = {
  record?: FullEditorialAsset;
  isReadOnlyMode?: boolean;
  isImportMode?: boolean;
  t?: TFunction;
};

export type EditorConfigFn = (args: EditorConfigArgs) => EditorSetupConfig;

export type EditorConfigsMap = {
  [K in EditorField]: EditorConfigFn;
};

// If for some reason you need to loop through all can use this lookup map...
// eg. const authorConfig = getEditorConfigsMap['authors'](yourRecord);
// for (const [field, getConfig] of Object.entries(getEditorConfigsMap)) {
//   const config = getConfig(recordCopy);
// });

export const getEditorConfigsMap: EditorConfigsMap = {
  title: getTitleConfig,
  content: getContentConfig,
  authors: getAuthorsConfig,
  byline: getBylineConfig,
  sections: getSectionsConfig,
  summary: getSummaryConfig,
  keywords: getKeywordsConfig,
  flags: getFlagsConfig,
  site_tags: getSiteTagsConfig,
  'properties.alttext': getAltTextConfig,
  'properties.html': getHtmlConfig,
  'properties.hammer': getHammerConfig,
  'properties.kicker': getKickerConfig,
  'properties.subheadline': getSubheadlineConfig,
  'properties.link': getLinkConfig,
  'properties.priority': getPriorityConfig,
  'properties.source': getSourceConfig,
  'properties.print_headline': getPrintHeadlineConfig,
  'properties.print_jump_head': getPrintJumpHeadConfig,
  'properties.print_jump_keyword': getPrintJumpKeywordConfig,
  'properties.print_notes': getPrintNotesConfig,
  'properties.print_placement_priority': getPrintPlacementPriorityConfig,
  'properties.print_slug': getPrintSlugConfig,
  'properties.print_subheadline': getPrintSubheadlineConfig,
  'properties.address': getAddressConfig,
  'properties.municipality': getMunicipalityConfig,
  'properties.postal_code': getPostalCodeConfig,
  'properties.latitude': getLatitudeConfig,
  'properties.longitude': getLongitudeConfig,
  'properties.tagline': getTaglineConfig,
  'properties.seo_title': getSeoTitleConfig,
  'properties.seo_description': getSeoDescriptionConfig,
  'properties.canonical_url': getCanonicalUrlConfig,
  'properties.url_title': getUrlTitleConfig,
  'properties.social_title': getSocialTitleConfig,
  'properties.social_summary': getSocialSummaryConfig,
};
